import { useState } from "react";
import { api, formatError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import { Download, Upload, DatabaseBackup, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ORDER = ["users", "products", "stores", "routes", "visits", "orders", "payments", "no_order_reports", "stock_movements"];
const LABELS = {
  users: "Users (admins & salesmen)", products: "Products", stores: "Stores", routes: "Routes",
  visits: "Visits", orders: "Orders", payments: "Payments", no_order_reports: "No-order reports", stock_movements: "Stock movements",
};
const CHUNK = 300;

const Panel = ({ icon: Icon, title, desc, children }) => (
  <div className="rounded-xl border border-[color:var(--border)] bg-white p-6 ink-shadow space-y-4">
    <div className="flex items-start gap-3">
      <span className="h-10 w-10 rounded-lg flex items-center justify-center bg-[color:var(--ink)]/5 shrink-0">
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <h2 className="font-display text-lg md:text-xl font-bold">{title}</h2>
        <p className="text-sm text-[color:var(--muted)] mt-0.5">{desc}</p>
      </div>
    </div>
    {children}
  </div>
);

const ExportPanel = () => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/backup");
      const blob = new Blob([JSON.stringify(data)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `gm-kridam-backup-${data.exported_at.slice(0, 10)}.json`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 30_000);
      const total = Object.values(data.collections).reduce((n, c) => n + c.length, 0);
      toast.success(`Backup downloaded · ${total} records`);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel icon={Download} title="1. Download backup" desc="Run this in the app that has your data (e.g. the preview app). Saves every store, order, product, user, payment and route into one file.">
      <Button onClick={download} disabled={busy} className="h-12 bg-[color:var(--ink)] hover:bg-black text-white" data-testid="backup-download-button">
        {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
        {busy ? "Preparing…" : "Download backup file"}
      </Button>
    </Panel>
  );
};

const ResultsTable = ({ results }) => (
  <div className="rounded-lg border border-[color:var(--border)] overflow-hidden text-sm" data-testid="restore-results">
    <table className="w-full">
      <thead className="bg-[color:var(--surface-2)] text-xs uppercase text-[color:var(--muted)]">
        <tr><th className="text-left px-3 py-2">Data</th><th className="text-right px-3 py-2">In file</th><th className="text-right px-3 py-2">Added</th><th className="text-right px-3 py-2">Matched</th><th className="text-right px-3 py-2">Already there</th></tr>
      </thead>
      <tbody>
        {ORDER.filter((c) => results[c]).map((c) => (
          <tr key={c} className="border-t border-[color:var(--border)]" data-testid={`restore-row-${c}`}>
            <td className="px-3 py-2">{LABELS[c]}</td>
            <td className="px-3 py-2 text-right tabular-nums">{results[c].total}</td>
            <td className="px-3 py-2 text-right tabular-nums text-[color:var(--moss)] font-semibold">{results[c].inserted}</td>
            <td className="px-3 py-2 text-right tabular-nums">{results[c].replaced}</td>
            <td className="px-3 py-2 text-right tabular-nums text-[color:var(--muted)]">{results[c].skipped}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ImportPanel = () => {
  const [backup, setBackup] = useState(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const data = JSON.parse(await f.text());
      if (data.app !== "gm-kridam-distribution" || !data.collections) throw new Error();
      setBackup(data);
      setResults(null);
    } catch {
      toast.error("This is not a valid GM Kridam backup file");
    }
  };

  const restore = async () => {
    if (!window.confirm("Copy this backup into this app? Existing data is kept; only missing records are added.")) return;
    setRunning(true);
    setProgress(0);
    const out = {};
    const total = ORDER.reduce((n, c) => n + (backup.collections[c]?.length || 0), 0) || 1;
    let done = 0;
    try {
      for (const c of ORDER) {
        const docs = backup.collections[c] || [];
        out[c] = { total: docs.length, inserted: 0, replaced: 0, skipped: 0 };
        for (let i = 0; i < docs.length; i += CHUNK) {
          const { data } = await api.post(`/admin/restore/${c}`, { docs: docs.slice(i, i + CHUNK) });
          out[c].inserted += data.inserted;
          out[c].replaced += data.replaced;
          out[c].skipped += data.skipped;
          done += Math.min(CHUNK, docs.length - i);
          setProgress(Math.round((done / total) * 100));
        }
      }
      setResults(out);
      toast.success("Data transfer complete");
    } catch (e) {
      setResults(out);
      toast.error(`Transfer stopped: ${formatError(e)}`);
    } finally {
      setRunning(false);
    }
  };

  return (
    <Panel icon={Upload} title="2. Restore into this app" desc="Run this in the live app. Upload the backup file. Records already here are kept, and anything missing is added. Users, products and stores with the same email or name are matched up instead of duplicated.">
      <label className="flex items-center justify-center gap-2 h-12 rounded-lg border-2 border-dashed border-[color:var(--border)] cursor-pointer hover:bg-[color:var(--surface-2)] text-sm font-semibold" data-testid="restore-file-label">
        <DatabaseBackup className="h-4 w-4" /> {backup ? "Choose a different file" : "Choose backup file (.json)"}
        <input type="file" accept="application/json,.json" className="hidden" onChange={pick} data-testid="restore-file-input" />
      </label>
      {backup && (
        <div className="space-y-3">
          <div className="text-sm" data-testid="restore-file-summary">
            Backup from <b>{new Date(backup.exported_at).toLocaleString("en-IN")}</b>:{" "}
            {ORDER.map((c) => `${backup.collections[c]?.length || 0} ${LABELS[c].split(" (")[0].toLowerCase()}`).join(" · ")}
          </div>
          {running && <Progress value={progress} data-testid="restore-progress" />}
          <Button onClick={restore} disabled={running} className="h-12 bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="restore-start-button">
            {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            {running ? `Copying… ${progress}%` : "Start data transfer"}
          </Button>
        </div>
      )}
      {results && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-[color:var(--moss)]"><CheckCircle2 className="h-4 w-4" /> Transfer summary</div>
          <ResultsTable results={results} />
        </div>
      )}
    </Panel>
  );
};

export default function Backup() {
  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-backup-page">
      <div>
        <p className="overline text-[color:var(--muted)]">Data transfer</p>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Backup &amp; restore</h1>
        <p className="text-[color:var(--muted)] mt-1">Move your data from the preview app to the live app, or keep a safe copy.</p>
      </div>
      <ExportPanel />
      <ImportPanel />
    </div>
  );
}
